/**
 * Clinics API — real backend (via axios).
 *
 * Entering a clinic. The customer is physically at a clinic and types or scans
 * the clinic's 6-digit code. "Entering" is a state change — it records the
 * customer ↔ clinic relationship (so the clinic can list its clients and the
 * user can see their clinic history, each with a last-entered date) — so it's a
 * POST, not a GET:
 *
 *   POST /user/clinic-visits   body { clinicCode }  → record entry + resolve clinic
 *   GET  /user/clinic-visits                        → my visited clinics (history)
 *
 * `resolveClinic` keeps its name (the ClinicEntryPage "resolves" a clinic from a
 * code) but under the hood it records the visit and returns the resolved clinic.
 * Backend response shape is unchanged from a pure lookup:
 *   { data: { clinic: { clinicCode, clinicName } } }
 * so callers don't change.
 *
 * Distinct from:
 *   - POST /services/:id/checkout — also validates a clinic code, but tied to a
 *     payment (the backend records a visit there too, so paying counts as a
 *     visit even without the explicit enter step).
 *   - GET /user/clinic-tickets/eligible-clinics — only clinics PAID at recently.
 *
 * All requests go through the shared axios instance (Bearer token, 401 refresh,
 * contract error shape err.response.data.error.{code,message,messageEn,details}).
 */

import axiosInstance from './axiosInstance';

/**
 * Enter a clinic by its 6-digit code: records the visit and returns the clinic.
 *
 * @param {string} code 6-digit numeric clinic code (digit-normalized + trimmed by the caller)
 * @returns {Promise<{ data: { clinic: { clinicCode: string, clinicName: (string|{fa?:string,en?:string}) } } }>}
 * @throws Axios-shaped error; notable code: 404 CLINIC_NOT_FOUND
 */
export async function resolveClinic(code) {
  const res = await axiosInstance.post('/user/clinic-visits', { clinicCode: code });
  return res.data;
}

/**
 * List the clinics the current user has visited, newest entry first.
 *
 * @returns {Promise<{ data: { clinics: Array<{ clinicCode, clinicName, firstEnteredAt, lastEnteredAt, entryCount }> }, meta: { total } }>}
 */
export async function listMyClinics() {
  const res = await axiosInstance.get('/user/clinic-visits');
  return res.data;
}

export const clinicsApi = { resolveClinic, listMyClinics };
export default clinicsApi;
